import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Books } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSeo, breadcrumbs } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const LABEL = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function CataloguePublic() {
  const [board, setBoard] = useState(null);
  const [kind, setKind] = useState("");

  useEffect(() => {
    api.get("/catalogue/public", { params: kind ? { kind } : {} })
      .then(({ data }) => setBoard(data))
      .catch(() => setBoard({ entries: [], kinds: [], categories: [] }));
  }, [kind]);

  useSeo({
    title: "Our catalogue | Unity & Utility",
    description: (board?.blurb || "Everything we sell — tools, machines, site equipment and workwear, with prices and pictures.").slice(0, 180),
    path: "/catalogue",
    image: board?.picture ? `${BACKEND}${board.picture}` : undefined,
    jsonLd: breadcrumbs([["Home", "/"], ["Catalogue", "/catalogue"]]),
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14" data-testid="catalogue-public-page">
        <Crumbs items={[["Home", "/"], ["Catalogue", "/catalogue"]]} />
        <p className="overline text-[#B3241B]">What we sell</p>
        <h1 className="font-display mt-2 flex items-start gap-3 text-4xl font-bold leading-[1.05] tracking-tight text-[#0F172A] sm:text-5xl">
          <Books size={36} className="mt-1 shrink-0 text-[#002FA7]" weight="duotone" />
          Our catalogue
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-700" data-testid="catalogue-public-blurb">
          {board?.blurb || "Tools, machines, site equipment and workwear from a working supplier — priced, pictured and kept current."}
        </p>
        <div className="mt-5">
          <ShareBar url={`${BACKEND}/api/share/catalogue`} text="Our catalogue"
            testid="catalogue-public-share" instagram />
        </div>

        {board?.kinds?.length > 0 && (
          <div className="scroll-x mt-8 flex gap-1.5 overflow-x-auto" data-testid="catalogue-public-filter">
            {[["", "Everything"], ...board.kinds.map((k) => [k.key, k.label])].map(([key, label]) => (
              <button key={key || "all"} data-testid={`catalogue-public-kind-${key || "all"}`} onClick={() => setKind(key)}
                className={`tap-safe shrink-0 border px-2.5 py-1.5 text-xs font-semibold transition-colors duration-150 ${
                  kind === key ? "border-[#0F172A] bg-[#0F172A] text-white"
                    : "border-border bg-white text-slate-600 hover:border-[#0F172A] hover:text-[#0F172A]"}`}>
                {label}
              </button>
            ))}
          </div>
        )}

        {board?.entries?.length === 0 ? (
          <p className="mt-8 text-sm text-slate-500" data-testid="catalogue-public-empty">
            Nothing in the catalogue for that filter just now.
          </p>
        ) : (
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3" data-testid="catalogue-public-list">
            {board?.entries?.map((e, i) => (
              <div key={`${e.kind}-${e.id}`} data-testid={`catalogue-public-${i}`} className="border border-border bg-white">
                {e.image_url && (
                  <Link to={`/catalogue/${e.kind}/${e.id}`}>
                    <img src={`${BACKEND}${e.image_url}`} alt={e.name} className="h-44 w-full object-cover" />
                  </Link>
                )}
                <div className="p-3">
                  <p className="text-[11px] uppercase tracking-wide text-slate-500">
                    {e.kind_label}{e.category ? ` · ${LABEL(e.category)}` : ""}
                  </p>
                  <Link to={`/catalogue/${e.kind}/${e.id}`} className="mt-1 block font-semibold text-[#0F172A] hover:text-[#002FA7]">
                    {e.name}
                  </Link>
                  <p className="mt-1 text-sm font-bold text-[#0B5C2E]">{e.symbol}{e.price}</p>
                  <div className="mt-2">
                    <ShareBar url={`${BACKEND}/api/share/catalogue/${e.kind}/${e.id}`} text={e.name}
                      testid={`catalogue-public-share-${i}`} compact instagram />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PublicShell>
  );
}

export function CatalogueEntryPublic() {
  const { kind, id } = useParams();
  const [entry, setEntry] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/catalogue/public/${kind}/${id}`)
      .then(({ data }) => setEntry(data))
      .catch((e) => setError(formatApiErrorDetail(e?.response?.data?.detail) || "Not in the catalogue"));
  }, [kind, id]);

  useSeo({
    title: entry ? `${entry.name} | Catalogue | Unity & Utility` : "Catalogue | Unity & Utility",
    description: (entry?.description || entry?.name || "From our catalogue.").slice(0, 180),
    path: `/catalogue/${kind}/${id}`,
    image: entry?.image_url ? `${BACKEND}${entry.image_url}` : undefined,
    jsonLd: breadcrumbs([["Home", "/"], ["Catalogue", "/catalogue"], [entry?.name || "Line", null]]),
  });

  return (
    <PublicShell>
      <div className="mx-auto max-w-3xl px-5 py-10 sm:px-8 sm:py-14" data-testid="catalogue-entry-page">
        <Crumbs items={[["Home", "/"], ["Catalogue", "/catalogue"], [entry?.name || "Line", null]]} />
        {error && <p data-testid="catalogue-entry-error" className="mt-6 text-sm text-slate-500">{error}</p>}
        {entry && (
          <>
            <p className="overline text-[#B3241B]">{entry.kind_label}{entry.category ? ` · ${LABEL(entry.category)}` : ""}</p>
            <h1 data-testid="catalogue-entry-title" className="font-display mt-2 text-3xl font-bold leading-[1.1] tracking-tight text-[#0F172A] sm:text-4xl">
              {entry.name}
            </h1>
            {entry.image_url && (
              <img data-testid="catalogue-entry-image" src={`${BACKEND}${entry.image_url}`} alt={entry.name}
                className="mt-6 w-full border border-border object-cover" />
            )}
            <p data-testid="catalogue-entry-price" className="mt-5 text-2xl font-bold text-[#0B5C2E]">
              {entry.symbol}{entry.price}
              {entry.quantity != null && (
                <span className="ml-2 align-middle text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {entry.quantity} {entry.unit || ""} available{entry.seller ? ` · ${entry.seller}` : ""}
                </span>
              )}
            </p>
            {entry.description && (
              <p className="mt-4 whitespace-pre-line text-base leading-relaxed text-slate-700">{entry.description}</p>
            )}
            <div className="mt-6">
              <ShareBar url={`${BACKEND}/api/share/catalogue/${kind}/${id}`} text={entry.name}
                testid="catalogue-entry-share" instagram />
            </div>
            <Link to="/catalogue" data-testid="catalogue-entry-back"
              className="mt-8 inline-block text-sm font-semibold text-[#002FA7] hover:underline">
              See the whole catalogue
            </Link>
          </>
        )}
      </div>
    </PublicShell>
  );
}
